import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import type { SortDirection } from '@/types/platform';
import styles from './platform-ui.module.css';

/**
 * Paging and sorting, done server-side and said out loud.
 *
 * WHY THE TOTAL IS SHOWN AND NOT JUST THE PAGE
 * The console previously folded "open cases" out of whatever the first response
 * happened to contain. That is right until the list outgrows one page, and then it
 * is quietly, permanently wrong — the screen reports thirty open cases because
 * thirty is what fitted, and nobody notices because the number is plausible. So
 * every list now states the range it is showing and the total the server counted,
 * and if the server did not return a total the caller passes what it knows rather
 * than a fabricated one.
 *
 * WHY SORTING IS A REQUEST, NOT A CLIENT-SIDE SORT
 * Sorting one page reorders one page. On a list of two hundred institutions,
 * "oldest first" that only sorts the twenty-five in front of you is not a weaker
 * feature, it is a wrong answer to the question being asked.
 */

interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  onPage: (page: number) => void;
  /** Announced label, e.g. "Audit events". Used to disambiguate two lists on a page. */
  label: string;
}

export function Pagination({ page, limit, total, totalPages, onPage, label }: PaginationProps) {
  const { t } = useTranslation();

  if (total === 0) return null;

  const first = (page - 1) * limit + 1;
  const last = Math.min(total, page * limit);
  const pages = Math.max(1, totalPages);

  return (
    <nav className={styles.pager} aria-label={label}>
      <p className={styles.pagerRange} role="status" aria-live="polite">
        {t('platform.paging.range', { first, last, total })}
      </p>
      <div className={styles.pagerControls}>
        <button
          type="button"
          className={styles.pagerBtn}
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label={t('platform.paging.previous')}
        >
          <Icon name="chevronLeft" size={15} aria-hidden="true" />
          <span>{t('platform.paging.previous')}</span>
        </button>
        <span className={styles.pagerPosition}>{t('platform.paging.page', { page, pages })}</span>
        <button
          type="button"
          className={styles.pagerBtn}
          onClick={() => onPage(page + 1)}
          disabled={page >= pages}
          aria-label={t('platform.paging.next')}
        >
          <span>{t('platform.paging.next')}</span>
          <Icon name="chevronRight" size={15} aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}

interface SortableHeaderProps {
  /** The field name the server sorts on. */
  field: string;
  label: string;
  activeField: string | undefined;
  direction: SortDirection;
  onSort: (field: string, direction: SortDirection) => void;
}

/**
 * A sortable column header.
 *
 * `aria-sort` on the `th` is what makes this usable without sight: without it the
 * only signal that a column is sorted is a small arrow, and the only signal that a
 * header is actionable is that it happens to be a button.
 */
export function SortableHeader({ field, label, activeField, direction, onSort }: SortableHeaderProps) {
  const { t } = useTranslation();
  const active = activeField === field;
  const next: SortDirection = active && direction === 'ASC' ? 'DESC' : 'ASC';

  return (
    <th scope="col" aria-sort={active ? (direction === 'ASC' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        className={`${styles.sortBtn} ${active ? styles.sortBtnActive : ''}`}
        onClick={() => onSort(field, next)}
        aria-label={
          next === 'ASC'
            ? t('platform.paging.sortAscending', { column: label })
            : t('platform.paging.sortDescending', { column: label })
        }
      >
        <span>{label}</span>
        <span className={styles.sortMark} aria-hidden="true">
          {active ? (direction === 'ASC' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}
